import React, { useState, useEffect, useRef } from 'react';
import '../css/otp.css';

const OTPVerification = ({ userEmail, onBack, onVerify, onResendOTP, otpResponse, isOtpSending, onVerificationSuccess }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeRemaining, setTimeRemaining] = useState(180); // 3 minutes
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resendCount, setResendCount] = useState(0);
  const [showLimitExceed, setShowLimitExceed] = useState(false);
  const [otpStatusMessage, setOtpStatusMessage] = useState('');
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isOtpSending) {
      setOtpStatusMessage('OTP sending...');
      setErrorMessage('');
    } else if (otpResponse) {
      if (otpResponse.otp_sent === true) {
        setOtpStatusMessage('OTP sent successfully!');
        setErrorMessage('');
      } else if (otpResponse.otp_sent === false) {
        setOtpStatusMessage('');
        setErrorMessage('Invalid email address. Please verify and try again.');
      }
    }
  }, [otpResponse, isOtpSending]);

  useEffect(() => {
    return () => {
      setOtpStatusMessage('');
      setErrorMessage('');
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onBack();
          return 0;
        }
        if (prev === 150) {
          setIsResendDisabled(false);
        }
        
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onBack]);

  // Time format in MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    // Clear error message when user starts typing
    if (errorMessage) {
      setErrorMessage('');
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle Enter key to verify OTP
    if (e.key === 'Enter') {
      e.preventDefault();
      const otpString = otp.join('');
      if (otpString.length === 6) {
        handleVerify();
      }
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, 6);
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
    }
  };

  // Handle verify
  const handleVerify = async () => {
    console.log('=== OTPVerification: handleVerify called ===');
    const otpString = otp.join('');
    if (otpString.length === 6) {
      setIsVerifying(true);
      setErrorMessage('');
      try {
        // First verify the OTP
        const apiResponseData = await onVerify(otpString);
        // Show verified state for 2 seconds before redirecting
        setIsVerified(true);
        // Don't call onVerify again - just wait and then call success callback
        setTimeout(() => {
          // Call the success callback to trigger navigation
          console.log('=== OTPVerification: Calling onVerificationSuccess ===');
          if (onVerificationSuccess) {
            console.log('=== OTPVerification: onVerificationSuccess exists, calling it ===');
            onVerificationSuccess(apiResponseData);
          } else {
            console.log('=== OTPVerification: onVerificationSuccess is undefined ===');
          }
        }, 2000);
      } catch (error) {
        setErrorMessage('Incorrect OTP entered. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } finally {
        setIsVerifying(false);
      }
    }
  };

  const handleResend = async () => {
    if (!isResendDisabled && resendCount < 2) {
      setIsResending(true);
      setErrorMessage('');
      setOtpStatusMessage('OTP sending...');
      
      try {
        await onResendOTP();
        const newResendCount = resendCount + 1;
        setResendCount(newResendCount);
        setTimeRemaining(180);
        setOtp(['', '', '', '', '', '']);
        setIsResendDisabled(true);
        setTimeout(() => {
          if (newResendCount < 2) {
            setIsResendDisabled(false);
          }
        }, 30000);
        
        if (newResendCount >= 2) {
          setShowLimitExceed(true);
        }
        
        setOtpStatusMessage('OTP resent successfully!');
        
      } catch (error) {
        setErrorMessage('Failed to resend OTP. Please try again.');
        setOtpStatusMessage('');
        console.error('OTP resend error:', error);
      } finally {
        setIsResending(false);
      }
    }
  };

  const handleBack = () => {
    setOtp(['', '', '', '', '', '']);
    setTimeRemaining(180);
    setIsResendDisabled(true);
    setResendCount(0);
    setShowLimitExceed(false);
    setErrorMessage('');
    onBack();
  };

  return (
    <div className="otp-content-wrapper px-3 md:px-4">
      <button onClick={handleBack} className="otp-back-button">
        <i className="fa-solid fa-chevron-left mr-2"></i>
        Back
      </button>

      <h2 className="otp-title text-lg md:text-xl">Verify your Account</h2>

      <p className="otp-instruction text-base md:text-lg">
        A Verification code has been {resendCount > 0 ? 'resent to' : 'sent to'}{' '}
        <span className="otp-email">{userEmail}</span><br/>
        Please use it to confirm your email address.
      </p>

      {otpStatusMessage && (
        <div className="mt-1 mb-2 text-center">
          <span className={`font-medium ${
            otpStatusMessage === 'OTP sending...' 
              ? 'text-orange-500' 
              : 'text-green-600'
          }`}>
            {otpStatusMessage}
          </span>
        </div>
      )}

      <div className="otp-input-container gap-2 md:gap-3">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            maxLength="1"
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className="otp-input w-10 h-10 md:w-12 md:h-12 text-lg md:text-xl"
            disabled={otpResponse?.otp_sent === false}
          />
        ))}
      </div>

      <div className={`otp-timer ${timeRemaining <= 30 ? 'otp-timer-warning' : ''}`}>
        Time remaining: <span className="otp-time">{formatTime(timeRemaining)}</span>
        {timeRemaining <= 30 && <span className="otp-warning-text"> (Hurry up!)</span>}
      </div>

      {errorMessage && (
        <div className="mt-1 mb-2 text-center">
          <span className="text-red-600 font-medium">{errorMessage}</span>
        </div>
      )}

      <button
        onClick={handleVerify}
        disabled={otp.join('').length !== 6 || isVerifying || isVerified || otpResponse?.otp_sent === false}
        className="otp-verify-button h-11 md:h-12 text-sm md:text-base"
        style={
          isVerified 
            ? { backgroundColor: '#16a34a', color: 'white' }
            : (isVerifying || otp.join('').length === 6) 
              ? { backgroundColor: '#F1593F', color: 'white' }
              : {}
        }
      >
        {isVerified ? 'VERIFIED' : isVerifying ? 'VERIFYING...' : 'VERIFY'}
      </button>

      <div className="otp-resend">
        {showLimitExceed ? (
          <span className="otp-limit-exceed">Resend limit exceeded. Please go back and try again.</span>
        ) : (
          <>
            Didn't Receive It?{' '}
            <button
              onClick={handleResend}
              disabled={isResendDisabled || isResending || otpResponse?.otp_sent === false}
              className="otp-resend-link"
            >
              {isResending ? 'Sending...' : `Resend OTP (${2 - resendCount} attempts left)`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OTPVerification; 